import * as React from 'react';
import {
  useLocation,
  //  useNavigate
} from 'react-router';

import { Drawer } from '@/components';
import { PaymentStatusBanner } from '@/components/payment-status-banner/payment-status-banner';
import { TrialBanner } from '@/components/trial-banner/trial-banner';
import { Button } from '@/components/ui/button';
import { NotificationBanner } from '@/components/ui/notification-banner/notification-banner';
import { getRouteLayoutOptions } from '@/config/layout-routes';
import { paths } from '@/config/paths';
import { BatchSelection } from '@/features/batch-selection';
import { BhanzuBuddy } from '@/features/bhanzu-buddy';
import { GuidedTour } from '@/features/guided-tour';
import { useProfilesData } from '@/features/user';
import { useBhanzuBuddyFeatures } from '@/lib/feature-flags';
import { useIsMobileOrTablet } from '@/lib/hooks/use-media-query';
import { useBatchSelectionDrawerStore } from '@/stores/batch-selection-drawer';
import { useTourStore } from '@/stores/tour';
import { cn } from '@/utils';

import { Footer } from './components/footer/footer';
import { MainHeader, MobileHeader, MainHeaderProps } from './components/header';

export interface MainLayoutProps {
  children: React.ReactNode;
  className?: string;
  headerProps?: Omit<MainHeaderProps, 'className'>;
  headerClassName?: string;
}

const MainLayout = React.forwardRef<HTMLDivElement, MainLayoutProps>(
  ({ children, className, headerProps, headerClassName, ...props }, ref) => {
    const isMobileOrTablet = useIsMobileOrTablet();
    const { selectedProfile, onboardingCourses, isOnboardingCandidate } =
      useProfilesData();
    const isDrawerOpen = useBatchSelectionDrawerStore(
      state => state.isDrawerOpen
    );
    const setIsDrawerOpen = useBatchSelectionDrawerStore(
      state => state.setIsDrawerOpen
    );
    const { showBhanzuBuddy, enableQuickChallenge } = useBhanzuBuddyFeatures();
    const currentPathname = useLocation().pathname;
    const { nonStickyHeader } = getRouteLayoutOptions(currentPathname);

    // Check if the tour is currently running
    const { run: isTourRunning } = useTourStore();

    const isAnyBatchSelectionPending = onboardingCourses?.find(
      course => !course.batch_id
    );

    const showOnboardingBanner = React.useMemo(() => {
      return (
        isOnboardingCandidate &&
        isAnyBatchSelectionPending &&
        currentPathname !== paths.app.onboarding.path
      );
    }, [isOnboardingCandidate, isAnyBatchSelectionPending, currentPathname]);

    // Get variant IDs from enrolled courses that need batch assignment
    const variantIds =
      onboardingCourses
        ?.filter(course => !course.batch_id || course.batch_id.trim() === '')
        ?.map(course => ({
          variantId: course.variant_id,
          courseName: course.course_name,
        })) ?? [];

    return (
      <>
        <GuidedTour />

        {/* Banner stack — full-bleed at the top of the page. On mobile the header
            is fixed, so this wrapper carries the header clearance (pt-36) to keep
            the banners (and the content that follows) below the fixed header. */}
        <div
          className={cn(
            'bg-white',
            // Breathing room below the banners on mobile — only when a banner is
            // actually rendered (`:not(:empty)`), so the empty header spacer keeps
            // its exact clearance with no extra gap.
            isMobileOrTablet ? '[&:not(:empty)]:mb-4' : '',
            // `pt-36` is exactly the fixed-header clearance on mobile (keeps the
            // empty spacer flush). When a banner actually renders, bump the top
            // padding so there's a visible gap between the header and the banner.
            isMobileOrTablet && !nonStickyHeader
              ? 'pt-36 [&:not(:empty)]:pt-40'
              : ''
          )}
        >
          {/* Payment Status Banner - shown above trial banner */}
          <PaymentStatusBanner />

          {/* Trial Banner - shown only if the selected course is on trial */}
          <TrialBanner />

          {/* Onboarding batch-selection banner */}
          {showOnboardingBanner ? (
            <NotificationBanner
              title={`Hey ${selectedProfile?.name}! Your ${isAnyBatchSelectionPending?.subject} Course batch selection is pending.`}
              variant="error"
              action={
                <Button
                  variant="outline"
                  size="sm"
                  color="light"
                  onClick={() => setIsDrawerOpen(true)}
                >
                  Select Batch now
                </Button>
              }
            />
          ) : null}
        </div>

        {/* Layout */}

        {/* Mobile/Tablet Header - Fixed outside container (unless the route
            opts out via `nonStickyHeader`, in which case it renders in flow). */}
        {isMobileOrTablet ? (
          <MobileHeader nonSticky={nonStickyHeader} {...headerProps} />
        ) : null}

        <div
          ref={ref}
          className={cn(
            'min-h-screen bg-white flex flex-col max-w-(--max-width) mx-auto lg:px-16 md:px-8 px-4 mb-20',
            className
          )}
          {...props}
        >
          {/* Desktop Header */}
          {isMobileOrTablet ? null : (
            <div
              className={cn(
                'relative top-0 z-(--z-header) w-full py-6',
                isTourRunning || nonStickyHeader ? 'static' : 'lg:sticky'
              )}
            >
              <MainHeader
                className={cn('shadow-sm', headerClassName)}
                {...headerProps}
              />
            </div>
          )}

          {/* Main Content */}
          <main className="flex-1 flex flex-col gap-4 pb-6">{children}</main>

          {/* Bhanzu Buddy - Only show when user has selected profile and feature
              is enabled. Per-route opt-outs live in `config/layout-routes.ts`
              and are already baked into `showBhanzuBuddy`. */}
          {selectedProfile?.id && showBhanzuBuddy ? (
            <BhanzuBuddy
              id="tour-bhanzu-buddy"
              leadId={selectedProfile.id}
              defaultMode="explain"
              enableQuickChallenge={enableQuickChallenge}
              onMessageSent={_response => {
                // Handle successful message
              }}
              onError={_error => {
                // Handle error
              }}
              placeholder="Ask me anything about your studies..."
            />
          ) : null}
        </div>

        <Drawer
          showCloseButton={true}
          isOpen={isDrawerOpen}
          onClose={() => setIsDrawerOpen(false)}
          borderRadius="pt-0 px-0 lg:px-0 bg-white"
          childClass="max-w-(100%)"
          innerContentClassName="p-0"
        >
          <BatchSelection
            profileId={selectedProfile?.id ?? ''}
            leadId={selectedProfile?.base_lead_id ?? ''}
            variantIds={variantIds}
            country={selectedProfile?.country ?? 'IN'}
            userEmail={selectedProfile?.communication_email_id ?? ''}
            selectedProfile={selectedProfile}
            onboardingCourses={onboardingCourses}
          />
        </Drawer>

        {isMobileOrTablet ? <Footer /> : null}
      </>
    );
  }
);

MainLayout.displayName = 'MainLayout';

export { MainLayout };
